'use client';

import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { MapPin } from 'lucide-react';
import { css, cva, cx } from '@/styled-system/css';
import { LucideIcon } from '@/src/components/LucideIcon';
import type { AreaResponse, AutocompleteResult } from '@/src/lib/api';
import LocationSearchModal from './LocationSearchModal';
import { formatAreaLabel, type LocationAnalyticsSurface } from './useLocationSuggestions';

/*
 * Matches the Combobox trigger's pill so the bar looks identical either side of
 * `lg`. One recipe rather than three inlined `css` objects: the three variants
 * were identical but for a single padding property each, and `c11nLint/no-dynamic-css-args`
 * rules out spreading a shared base into `css`. `cva` is Panda's own answer to
 * that — statically extracted, no spread — so the shared 14 properties, the
 * focus ring and the tap-area overlay are written once.
 */
const trigger = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: 'tighter',
    width: '100%',
    minWidth: 0,
    borderRadius: 'pill',
    backgroundColor: 'bg.action.neutral.alt.default',
    cursor: 'pointer',
    textAlign: 'start',
    // Same 40px box as the `sm` Button used by Filter and Save beside it: 8px
    // either side of a 24px `body` line box.
    paddingBlock: 'tighter',
    // Constellation's own tap-area treatment, copied from its `sm` Button: an
    // overlay, not a taller box. `min-block-size: 44px` would meet WCAG 2.5.5 by
    // growing the pill past the 40px Filter and Save buttons beside it, so the
    // hit area is extended outside the layout box instead.
    position: 'relative',
    _after: {
      content: '""',
      position: 'absolute',
      height: 'tapArea.mobile.default',
      minWidth: 'tapArea.mobile.default',
      width: '100%',
      left: '0',
      top: '50%',
      marginTop: 'calc(({sizes.tapArea.mobile.default} / 2) * -1)',
    },
    // Constellation's own focus treatment, not the browser's default outline —
    // otherwise this field focuses blue while the combobox next to it focuses
    // teal. `shadows.focus` is the same token c11n's own controls use.
    _focusVisible: {
      outline: 'none',
      boxShadow: 'focus',
    },
  },
  variants: {
    /*
     * Both values match what `LocationCombobox` resolves to at the same size,
     * because on desktop this field is only ever the pre-hydration paint of
     * that Combobox (`useIsDesktop` reports mobile until hydration settles).
     * Any disagreement here is a layout shift the user sees on every SRP and
     * HDP load, so keep the two in step:
     *   - `lg`  → Combobox.Trigger's own `size: lg` recipe, paddingInline 16px
     *   - `sm`  → LocationCombobox's `paddingInlineStart: 'default'`, 16px
     * The bar container only pads to `tightest` (4px), which is not enough to
     * hold the pin clear of the pill's curve on its own.
     */
    inset: {
      /** Homepage hero: inset on both sides. */
      both: { paddingInline: 'default' },
      /** SRP/HDP bar, sticky and header alike: owns its leading inset. */
      start: { paddingInlineStart: 'default' },
    },
  },
  defaultVariants: {
    inset: 'start',
  },
});

const label = css({
  textStyle: 'body',
  color: 'text.neutral',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const srOnly = css({ srOnly: true });
const labelBold = css({ fontWeight: 'bold' });
const placeholder = css({ color: 'text.subtle', fontWeight: 'regular' });

/** Mirrors `LocationCombobox`, so the bar can swap one for the other. */
export type LocationInputProps = {
  /** Currently selected area, shown as the field's value. */
  area: AreaResponse | AutocompleteResult | null;
  /** IP-derived city for the "Near Me" affordance in the dialog. */
  nearMeArea?: AreaResponse | null;
  /** Called when the user picks a suggestion or "Near Me". */
  onSelect: (result: AutocompleteResult) => void;
  /** Called when Enter should run the search for a suggestion right away. */
  onEnterSearch: (result: AutocompleteResult) => void;
  /** Called on every keystroke so the parent can cancel recent-search seeding. */
  onUserInput: () => void;
  size?: 'sm' | 'lg';
  analyticsSurface?: LocationAnalyticsSurface;
  /**
   * Accepted for prop parity with `LocationCombobox` (the bar spreads one
   * props object into whichever field it renders), but unused here: both SRP
   * bars take the same leading inset, and only the Combobox has a clear
   * control whose spacing compact has to correct.
   */
  compact?: boolean;
  testIdSuffix?: string;
};

/**
 * The location field below `lg`: a button that opens the full-screen search
 * (HP-8998), styled to match the Combobox trigger it stands in for.
 *
 * Deliberately not a Combobox. Tapping this opens a dialog, so the element is a
 * button with `aria-haspopup="dialog"` — not an input reporting
 * `role="combobox"` for a listbox it will never show. It also means no
 * floating-ui, no suggestion fetching and no options work runs in the bar at
 * all; that lives in the dialog, which only mounts once opened.
 */
export default function LocationInput({
  area,
  nearMeArea,
  onSelect,
  onEnterSearch,
  onUserInput,
  size = 'sm',
  analyticsSurface = size === 'lg' ? 'homepage' : 'search',
  testIdSuffix,
}: LocationInputProps) {
  const tSearch = useTranslations('search');
  const areaLabel = formatAreaLabel(area);
  const triggerCss = trigger({ inset: size === 'lg' ? 'both' : 'start' });

  // The dialog is this field's own popup, the way the dropdown belongs to
  // LocationCombobox — the bar shouldn't have to know either exists. Mounted
  // only while open so dismissing discards its draft query.
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);

  return (
    <>
      <button
        // Inside ListingSearch's <form>, so it must not be the submit button.
        type="button"
        className={triggerCss}
        onClick={() => setIsOpen(true)}
        aria-haspopup="dialog"
        data-testid={testIdSuffix ? `location-search-trigger-${testIdSuffix}` : 'location-search-trigger'}
      >
        <LucideIcon icon={MapPin} size={size === 'lg' ? 'md' : 'sm'} />
        {/* Named by its content, not `aria-label` — that would replace the
            visible text, so the accessible name would never carry the selected
            area (WCAG 2.5.3 Label in Name, and the Combobox this stands in for
            announced its value). */}
        <span className={srOnly}>{tSearch('searchByLocation')}</span>
        <span className={cx(label, areaLabel ? labelBold : placeholder)}>
          {areaLabel || tSearch('findApartmentsIn')}
        </span>
      </button>
      {isOpen ? (
        <LocationSearchModal
          onClose={close}
          nearMeArea={nearMeArea}
          onSelect={onSelect}
          onEnterSearch={onEnterSearch}
          onUserInput={onUserInput}
          analyticsSurface={analyticsSurface}
        />
      ) : null}
    </>
  );
}
